import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Tabs, 
  Tab, 
  Card, 
  CardContent, 
  CardActions,
  Chip,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowIcon,
  Add as AddIcon,
  Insights as StrategyIcon,
  AssignmentTurnedIn as TargetIcon,
  Message as MessageIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { AdStrategyResponse, AdVariant, AdFormat, CampaignStrategy, getAdStrategy } from '../../services/adStrategistService';

interface CreationTabProps {
  strategy: AdStrategyResponse | null;
  onCreateNew: () => void;
}

const CreationTab: React.FC<CreationTabProps> = ({ strategy, onCreateNew }) => {
  const [selectedVariant, setSelectedVariant] = useState<number | null>(null);
  const [formatFilter, setFormatFilter] = useState<string>('all');
  const [copiedStatus, setCopiedStatus] = useState<Record<number, boolean>>({});
  const [isPolling, setIsPolling] = useState(false);
  const [currentStrategy, setCurrentStrategy] = useState<AdStrategyResponse | null>(strategy);
  const [error, setError] = useState<string | null>(null);
  
  // Set up polling for strategy updates if status is 'pending'
  useEffect(() => {
    if (strategy && strategy.status === 'pending' && strategy.id) {
      setIsPolling(true);
      
      const pollingInterval = setInterval(async () => {
        try {
          const updatedStrategy = await getAdStrategy(strategy.id);
          setCurrentStrategy(updatedStrategy);
          
          // If strategy is complete or has error, stop polling
          if (updatedStrategy.status === 'completed' || updatedStrategy.status === 'error') {
            clearInterval(pollingInterval);
            setIsPolling(false);
            
            if (updatedStrategy.status === 'error') {
              setError(`Error generating strategy: ${updatedStrategy.error_message || 'Unknown error'}`);
            }
          }
        } catch (error) {
          console.error('Error polling strategy status:', error);
          clearInterval(pollingInterval);
          setIsPolling(false);
          setError("Failed to check strategy status. Please try refreshing the page.");
        }
      }, 5000); // Poll every 5 seconds
      
      // Cleanup the interval if component unmounts
      return () => clearInterval(pollingInterval);
    }
  }, [strategy]);
  
  // Check if strategy is still loading
  const isLoading = !strategy || strategy.status === 'pending' || strategy.status === 'processing';
  const hasError = strategy?.status === 'error' || strategy?.status === 'failed';
  const isComplete = strategy?.status === 'completed';
  
  // If strategy is still loading, show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Generating your ad strategy...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          This may take a few minutes. Please wait.
        </Typography>
      </Box>
    );
  }
  
  // If there was an error, show error message
  if (hasError) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {strategy?.error_message || "An error occurred while generating your ad strategy."}
        </Alert>
        <Button 
          startIcon={<RefreshIcon />} 
          variant="contained" 
          onClick={onCreateNew}
        >
          Try Again
        </Button>
      </Box>
    );
  }
  
  // More comprehensive validation of the strategy object
  if (!currentStrategy.strategy) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', p: 5 }}>
        <Typography variant="h6" color="error" gutterBottom>Invalid strategy data: Missing strategy information</Typography>
        <Button variant="contained" onClick={onCreateNew}>
          Create New Strategy
        </Button>
      </Box>
    );
  }
  
  // Check if strategy has required properties
  const hasVariants = currentStrategy && currentStrategy.ad_variants && Array.isArray(currentStrategy.ad_variants);
  
  if (!hasVariants || currentStrategy.ad_variants.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', p: 5 }}>
        <Typography variant="h6" gutterBottom>No ad variants available</Typography>
        <Button variant="contained" onClick={onCreateNew}>
          Create New Strategy
        </Button>
      </Box>
    );
  }
  
  const handleCopyContent = (variantIndex: number, content: string) => {
    navigator.clipboard.writeText(content).then(() => {
      setCopiedStatus({ ...copiedStatus, [variantIndex]: true });
      setTimeout(() => {
        setCopiedStatus({ ...copiedStatus, [variantIndex]: false });
      }, 2000);
    });
  };
  
  const handleFormatFilterChange = (event: React.SyntheticEvent, newValue: string) => {
    setFormatFilter(newValue);
    setSelectedVariant(null);
  };
  
  const formatName = (format: string): string => {
    return format.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Safely filter variants
  const filteredVariants = currentStrategy.ad_variants.filter(variant => 
    formatFilter === 'all' || variant.format === formatFilter
  );
  
  const selectedVariantData = selectedVariant !== null && selectedVariant < currentStrategy.ad_variants.length
    ? currentStrategy.ad_variants[selectedVariant] 
    : null;
  
  const getFormatChipColor = (format: string) => {
    const formatMap: Record<string, any> = {
      [AdFormat.SOCIAL_MEDIA_POST]: { color: 'primary' },
      [AdFormat.SEARCH_AD]: { color: 'secondary' },
      [AdFormat.DISPLAY_AD]: { color: 'info' },
      [AdFormat.CHATBOT_SCRIPT]: { color: 'success' },
      [AdFormat.EMAIL_NEWSLETTER]: { color: 'warning' },
      [AdFormat.VIDEO_AD]: { color: 'error' }
    };
    
    return formatMap[format] || { color: 'default' };
  };
  
  // Helper function to safely display strategy data
  const getStrategyValue = (key: keyof CampaignStrategy, defaultValue: string = '—') => {
    if (!currentStrategy || !currentStrategy.strategy) return defaultValue;
    const value = currentStrategy.strategy[key];
    return value && value !== "" ? value : defaultValue;
  };
  
  // Helper function to safely display arrays from strategy
  const getStrategyArray = (key: keyof CampaignStrategy): string[] => {
    if (!currentStrategy || !currentStrategy.strategy) return [];
    const value = currentStrategy.strategy[key];
    if (!value || !Array.isArray(value) || value.length === 0) {
      // Return placeholder data if empty
      if (key === 'main_selling_points') {
        return ["No selling points specified. Add some in the Setup tab."];
      } else if (key === 'target_keywords') {
        return ["No keywords specified. Add some in the Setup tab."];
      }
      return [];
    }
    return value;
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Ad Strategy</Typography>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={onCreateNew}>
          Create New Strategy
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StrategyIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="h6">Strategy Overview</Typography>
            </Box>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Value Proposition
            </Typography>
            <Typography variant="body1" paragraph>
              {getStrategyValue('value_proposition', 'No value proposition specified. You can add this in the Setup tab.')}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Messaging Approach
            </Typography>
            <Typography variant="body1" paragraph>
              {getStrategyValue('messaging_approach', 'No messaging approach specified. You can add this in the Setup tab.')}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Tone and Voice
            </Typography>
            <Typography variant="body1">
              {getStrategyValue('tone_and_voice', 'No tone specified. You can add this in the Setup tab.')}
            </Typography>
          </Paper>
          
          <Paper elevation={1} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TargetIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Typography variant="h6">Key Selling Points</Typography>
            </Box>
            
            <List>
              {getStrategyArray('main_selling_points').map((point, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ArrowIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={point} />
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MessageIcon sx={{ color: 'primary.main', mr: 1 }} />
              Target Keywords
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {getStrategyArray('target_keywords').length > 0 ? (
                getStrategyArray('target_keywords').map((keyword, index) => (
                  <Chip key={index} label={keyword} size="small" />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No target keywords specified. Add some in the Setup tab.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={8}>
          <Paper elevation={1} sx={{ mb: 3 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={formatFilter} 
                onChange={handleFormatFilterChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="All Formats" value="all" />
                {Array.from(new Set((currentStrategy.ad_variants || []).map(v => v.format))).map(format => (
                  <Tab 
                    key={format} 
                    label={formatName(format)} 
                    value={format} 
                  />
                ))}
              </Tabs>
            </Box>
            
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {formatFilter === 'all' 
                  ? 'All Generated Ad Variants' 
                  : `${formatName(formatFilter)} Variants`}
              </Typography>
              
              <Grid container spacing={2}>
                {filteredVariants.map((variant, index) => (
                  <Grid item xs={12} sm={6} md={6} key={index}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        bgcolor: selectedVariant === index ? 'action.selected' : 'background.paper',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                      onClick={() => setSelectedVariant(index)}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Chip 
                            label={formatName(variant.format)} 
                            size="small"
                            {...getFormatChipColor(variant.format)}
                          />
                          {variant.target_platform && (
                            <Chip 
                              label={variant.target_platform} 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        <Typography variant="subtitle1" gutterBottom noWrap>
                          {variant.title || formatName(variant.format)}
                        </Typography>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {variant.content.slice(0, 100)}...
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
          
          {selectedVariantData && (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {selectedVariantData.title || formatName(selectedVariantData.format)}
                </Typography>
                <Button
                  startIcon={copiedStatus[selectedVariant] ? <CheckCircleIcon /> : <CopyIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => handleCopyContent(selectedVariant, selectedVariantData.content)}
                  color={copiedStatus[selectedVariant] ? "success" : "primary"}
                >
                  {copiedStatus[selectedVariant] ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip 
                  label={formatName(selectedVariantData.format)} 
                  size="small"
                  {...getFormatChipColor(selectedVariantData.format)}
                />
                {selectedVariantData.target_platform && (
                  <Chip 
                    label={selectedVariantData.target_platform} 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
              
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                borderRadius: 1, 
                whiteSpace: 'pre-wrap',
                mb: 2
              }}>
                <Typography variant="body1">
                  {selectedVariantData.content}
                </Typography>
              </Box>
              
              {selectedVariantData.call_to_action && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Call to Action</Typography>
                  <Chip label={selectedVariantData.call_to_action} />
                </Box>
              )}
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreationTab; 